import { useState, useEffect, useCallback } from 'react';

export const useScenarios = (supabase) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchScenarios = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      if (!user) {
        setScenarios([]);
        return;
      }

      // First, fetch all scenarios for the user
      const { data: userScenarios, error: scenariosError } = await supabase
        .from('scenarios')
        .select('*')
        .eq('user_id', user.id);
        
      if (scenariosError) throw scenariosError;

      if (!userScenarios || userScenarios.length === 0) {
        setScenarios([]);
        setLoading(false);
        return;
      }

      // Then get the stats for each scenario
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_scenario_stats', { user_id: user.id });
        
      if (statsError) throw statsError;

      // Merge the scenario data with the stats
      const scenariosWithStats = userScenarios.map(scenario => {
        // Find matching stats for this scenario
        const stats = statsData?.find(stat => stat.scenario_id === scenario.id) || {};
        
        return {
          ...scenario,
          total_scans: stats.scan_count || 0,
          last_scan_at: stats.last_used || null
        };
      });

      setScenarios(scenariosWithStats);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchScenarios();

    // Subscribe to realtime changes
    const subscription = supabase
      .channel('scenarios_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'scenarios' 
        }, 
        () => {
          fetchScenarios();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase, fetchScenarios]);

  const getScenarioStats = async (scenarioId) => {
    try {
      // Get total scans for this scenario
      const { count: scanCount, error: scanError } = await supabase
        .from('scans')
        .select('id', { count: 'exact' })
        .eq('scenario_id', scenarioId);
        
      if (scanError) throw scanError;
      
      // Get most recent scan for this scenario
      const { data: recentScan, error: recentError } = await supabase
        .from('scans')
        .select('created_at')
        .eq('scenario_id', scenarioId)
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (recentError) throw recentError;
      
      return {
        scanCount: scanCount || 0,
        lastUsed: recentScan && recentScan.length > 0 ? recentScan[0].created_at : null
      };
    } catch (error) {
      console.error('Error getting scenario stats:', error);
      throw error;
    }
  };

  const createScenario = async (scenarioData) => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Add user_id to scenario data if not present
      if (!scenarioData.user_id && user) {
        scenarioData.user_id = user.id;
      }

      // Don't generate token - let the database trigger handle it with proper format (scn_...)

      const { data, error } = await supabase
        .from('scenarios')
        .insert([scenarioData])
        .select();

      if (error) throw error;

      await fetchScenarios(); // Refresh the scenarios to get the stats
      return data[0];
    } catch (error) {
      console.error('Error creating scenario:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateScenario = async (id, updates) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('scenarios')
        .update(updates)
        .eq('id', id)
        .select();
      
      if (error) throw error;
      
      await fetchScenarios(); // Refresh the scenarios to get updated stats
      return data[0];
    } catch (error) {
      console.error('Error updating scenario:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteScenario = async (id) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('scenarios')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update the state to remove this scenario
      setScenarios(prevScenarios =>
        prevScenarios.filter(scenario => scenario.id !== id)
      );
    } catch (error) {
      console.error('Error deleting scenario:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const duplicateScenario = async (id) => {
    try {
      setLoading(true);

      // Fetch the scenario to duplicate
      const { data: originalScenario, error: fetchError } = await supabase
        .from('scenarios')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Create a copy with only the actual database columns
      // Don't provide token - let the database trigger generate it with the correct format
      const duplicateData = {
        name: `Copy of ${originalScenario.name}`,
        user_id: user?.id || originalScenario.user_id,
        description: originalScenario.description,
        validate_logic: originalScenario.validate_logic,
        create_logic: originalScenario.create_logic,
        display_logic: originalScenario.display_logic,
        offline_logic: originalScenario.offline_logic,
        offline: originalScenario.offline,
        last_modified_field: originalScenario.last_modified_field,
        old_airtable_data: originalScenario.old_airtable_data
      };

      // Create the new scenario
      const newScenario = await createScenario(duplicateData);

      return newScenario;
    } catch (error) {
      console.error('Error duplicating scenario:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    scenarios,
    loading,
    error,
    refreshScenarios: fetchScenarios,
    getScenarioStats,
    createScenario,
    updateScenario,
    deleteScenario,
    duplicateScenario
  };
};